/** Browser-side key release, AES-GCM decryption, and reveal caching. */
import {
  hiddenSystemManifest,
  hasSealedSystemReveal,
  parseSystemRef,
  registerHiddenSystemManifest,
  registerSealedSystem,
  validateHiddenSystemManifest,
  WORLD_GENERATION_VERSION,
  type SystemRevealV1,
} from "@hyperlight/shared";
import { getIdToken } from "../auth/firebase";

interface EncryptedManifestV1 {
  v: 1;
  alg: "A256GCM";
  iv: string;
  aad: string;
  ciphertext: string;
}

const tickets = new Map<string, string>();
const inflight = new Map<string, Promise<void>>();

function bytes(encoded: string): Uint8Array<ArrayBuffer> {
  const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - encoded.length % 4) % 4);
  const binary = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function validReveal(raw: unknown): raw is SystemRevealV1 {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  const o = raw as Record<string, unknown>;
  const ref = parseSystemRef(o.systemId);
  return ref !== null && o.generationVersion === WORLD_GENERATION_VERSION && o.keyVersion === 1
    && typeof o.manifestKey === "string" && /^[A-Za-z0-9_-]{43}$/.test(o.manifestKey)
    && typeof o.manifestUrl === "string" && o.manifestUrl.startsWith("/api/universe/manifest?")
    && (o.worldSeed === undefined || (typeof o.worldSeed === "string" && /^[A-Za-z0-9_-]{43}$/.test(o.worldSeed)))
    && (o.access === undefined || typeof o.access === "string");
}

export function accessTicketFor(systemId: string): string | null {
  return tickets.get(systemId) ?? null;
}

export function hasSystemReveal(systemId: string): boolean {
  const ref = parseSystemRef(systemId);
  return hiddenSystemManifest(systemId) !== null
    && (ref?.kind !== "sealed" || hasSealedSystemReveal(systemId));
}

export function acceptSystemReveal(reveal: SystemRevealV1): Promise<void> {
  if (!validReveal(reveal)) return Promise.reject(new Error("invalid system reveal"));
  // A repeat current-system release can refresh the short-lived reconnect
  // ticket even when world/manifest material is already cached.
  if (reveal.access) tickets.set(reveal.systemId, reveal.access);
  const existing = inflight.get(reveal.systemId);
  if (existing) return existing;
  const task = (async () => {
    const ref = parseSystemRef(reveal.systemId)!;
    if (ref.kind === "sealed") {
      if (!reveal.worldSeed || !registerSealedSystem(reveal.systemId, reveal.worldSeed)) {
        throw new Error("sealed reveal did not contain a valid world seed");
      }
    }
    if (hiddenSystemManifest(reveal.systemId)) return;
    const response = await fetch(reveal.manifestUrl, { cache: "force-cache" });
    if (!response.ok) throw new Error(`manifest download failed (${response.status})`);
    const encrypted = await response.json() as EncryptedManifestV1;
    if (encrypted?.v !== 1 || encrypted.alg !== "A256GCM"
      || typeof encrypted.iv !== "string" || typeof encrypted.aad !== "string"
      || typeof encrypted.ciphertext !== "string") throw new Error("invalid encrypted manifest");
    const expectedAad = `hyperlight:hidden:v1:${reveal.systemId}`;
    if (encrypted.aad !== expectedAad) throw new Error("manifest context mismatch");
    const key = await crypto.subtle.importKey("raw", bytes(reveal.manifestKey), "AES-GCM", false, ["decrypt"]);
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: bytes(encrypted.iv), additionalData: new TextEncoder().encode(expectedAad), tagLength: 128 },
      key,
      bytes(encrypted.ciphertext),
    );
    const manifest = validateHiddenSystemManifest(
      JSON.parse(new TextDecoder().decode(plain)),
      reveal.systemId,
    );
    if (!manifest) throw new Error("decrypted manifest failed validation");
    registerHiddenSystemManifest(manifest);
  })();
  inflight.set(reveal.systemId, task);
  void task.catch(() => inflight.delete(reveal.systemId));
  return task;
}

/** Restore a previously authorized sealed system before its synchronous
 *  StarSystem constructor runs during profile resume. */
export async function loadAuthorizedSystemReveal(systemId: string): Promise<void> {
  if (hasSystemReveal(systemId)) return;
  const token = await getIdToken();
  if (!token) throw new Error("authentication required for system reveal");
  const response = await fetch(`/api/universe/reveal?id=${encodeURIComponent(systemId)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  const body = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok || !validReveal(body.reveal)) {
    throw new Error(typeof body.error === "string" ? body.error : "system reveal unavailable");
  }
  await acceptSystemReveal(body.reveal);
}
